import React, {
    ReactElement,
    useEffect,
    useState
} from 'react';
import {FormState, useFormContext} from "react-hook-form";
import {InputMaskProps} from "@react-input/mask";
import InputError from './InputError';
import dynamic from "next/dynamic";
import AddonContent, {IAddonContentProps} from "@/app/components/atomic/Input/AddonContent";
import {IGenericInputProps, IGenericInputReturn} from "@/@types/GenericInputProps";

const TextInput = dynamic(() => import('./TextInput'), {ssr: false})

export interface ITextInputProps extends IGenericInputProps {
    borders?: keyof typeof BORDERS;
    wrapperClass?: string;
}

interface AppInputProps extends ITextInputProps {
    addonContent?: string | ReactElement;
    prefixContent?: AppInputProps["addonContent"];
    addonTheme?: IAddonContentProps["theme"];
    required?: boolean;
    debounce?: number | null;
    placeholder?: string;
    mask?: InputMaskProps["mask"];
    replacement?: InputMaskProps["replacement"];
    track?: InputMaskProps["track"];
    validation?: boolean;
    disabled?: boolean;
    ref?: any;
    noDisabledBorder?: boolean;
}

const BORDERS = {
    default: 'border border-blue-300 hover:border-blue-500 focus-within:border-blue-500',
    neutral: 'border border-rm-gray-light hover:border-rm-gray focus-within:border-rm-gray',
} as const;

const DEFAULT_BORDERS = 'default';

function AppTextInput({
    value,
    onChange,
    onClick,
    fieldName,
    addonContent,
    prefixContent,
    addonTheme,
    required,
    debounce,
    placeholder,
    validation = undefined,
    mask,
    replacement,
    track,
    inputProps = {},
    className,
    wrapperClass,
    borders = DEFAULT_BORDERS,
    disabled = false,
    noDisabledBorder = false,
    ref = null,
}: AppInputProps) {
    const [error, setError] = useState(null)
    const bordersTheme = BORDERS[borders];

    const [isFocused, setIsFocused] = useState(false);
    const {
        formState = {},
        setValue = () => {},
    }: { formState: FormState<any> | {}, setValue: Function } = useFormContext() || {}

    useEffect(() => {
        setValue(fieldName, value)
    }, []);

    const onChangeWithValidation = (inputReturn: IGenericInputReturn) => {
        setError(null)
        setValue(fieldName, (inputReturn as any).value)
        onChange(inputReturn)
    }
    const renderInput = () => (
        <TextInput
            ref={ref}
            value={value || ""}
            onChange={onChangeWithValidation}
            onClick={onClick}
            fieldName={fieldName}
            required={required}
            debounce={debounce}
            placeholder={placeholder}
            validation={validation}
            inputProps={inputProps}
            mask={mask}
            replacement={replacement}
            track={track}
            disabled={disabled}
            className={`${prefixContent ? 'pl-0' : ''} ${className}`}
        />
    )

    return (
        <div
            onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)}
            className={`flex relative bg-white items-center rounded-md w-full min-h-12 transition-all duration-500
                ${error ? 'border-red-500' : ''} ${wrapperClass}
                ${disabled && !noDisabledBorder ? 'border !bg-white !border-gray-200 hover:border-gray-200 !text-gray-600' : bordersTheme}
            `}
        >
            {prefixContent && (
                <AddonContent theme={addonTheme}>
                    {prefixContent}
                </AddonContent>
            )}
            <InputError formState={formState} fieldName={fieldName} isFocused={isFocused} onError={setError}>
                {renderInput()}
            </InputError>
            {addonContent && (
                <AddonContent theme={addonTheme} place={'right'}>
                    {addonContent}
                </AddonContent>
            )}
        </div>
    );
}

export default React.memo(AppTextInput)
